import GuidSchema from "_shared/guids/guid-schema";
import { z } from "zod";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionSchema from "permissions/_schemas/permission-schema";
import Guid from "_shared/guids/guid";
import Permission from "permissions/permission";

export type PermissionsByGranteeJSON = z.infer<typeof PermissionsByGranteeSchema.PermissionsByGranteeJSONZ>;
export type PermissionsByGranteeParams = z.infer<typeof PermissionsByGranteeSchema.PermissionsByGranteeZ>;

export default class PermissionsByGranteeSchema {
  public static readonly PermissionsByGranteeJSONZ = z.object({
    id: z.uuid().nullish(),
    granteeUserId: z.uuid(),
    totalPermissions: z.number(),
    permissionCounts: z.record(z.string(), z.number()),
    recentPermissionsPreview: z.array(PermissionSchema.PermissionJSONZ),
  });

  public static readonly PermissionsByGranteeZ = z.object({
    id: GuidSchema.type,
    granteeUserId: GuidSchema.type,
    totalPermissions: z.number(),
    permissionCounts: z.record(z.string(), z.number()),
    recentPermissionsPreview: z.array(z.instanceof(Permission)),
  });

  public static validate(values: unknown): PermissionsByGranteeParams {
    return parseOrThrow(PermissionsByGranteeSchema.PermissionsByGranteeZ, values, {
      logError: true,
      schemaName: "PermissionsByGranteeParams",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): PermissionsByGranteeJSON {
    return parseOrThrow(PermissionsByGranteeSchema.PermissionsByGranteeJSONZ, values, {
      logError: true,
      schemaName: "PermissionsByGranteeJSON",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is PermissionsByGranteeParams {
    try {
      PermissionsByGranteeSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is PermissionsByGranteeJSON {
    try {
      PermissionsByGranteeSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: PermissionsByGranteeJSON): PermissionsByGranteeParams {
    const parsed = parseOrThrow(PermissionsByGranteeSchema.PermissionsByGranteeJSONZ, data, {
      logError: true,
      schemaName: "PermissionsByGranteeJSON",
      operation: "deserialize",
      payloadSample: data,
    });

    return PermissionsByGranteeSchema.codec.decode(parsed);
  }

  public static serialize(notification: PermissionsByGranteeParams): PermissionsByGranteeJSON {
    const parsed = PermissionsByGranteeSchema.validate(notification);
    return PermissionsByGranteeSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    PermissionsByGranteeSchema.PermissionsByGranteeJSONZ,
    PermissionsByGranteeSchema.PermissionsByGranteeZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        granteeUserId: Guid.fromJSON(data.granteeUserId)!,
        totalPermissions: data.totalPermissions,
        permissionCounts: data.permissionCounts,
        recentPermissionsPreview: Permission.fromJSONArray(data.recentPermissionsPreview),
      }),
      encode: (permissionsByGrantee) => ({
        id: permissionsByGrantee.id.toJSON(),
        granteeUserId: permissionsByGrantee.granteeUserId.toJSON(),
        totalPermissions: permissionsByGrantee.totalPermissions,
        permissionCounts: permissionsByGrantee.permissionCounts,
        recentPermissionsPreview: permissionsByGrantee.recentPermissionsPreview.map((p) => p.toJSON()),
      }),
    }
  );
}
